import type { RunAction } from '../api/client';
import type { ActionResult, EquippedCosmetic, RunState, SpectateFrame } from '../api/types';
import { spectateRenderRun } from '../api/types';
import {
  ENTITY_ASSETS,
  EFFECT_SPRITE_ASSETS,
  ENVIRONMENT_TILESETS,
  ITEM_PICKUP_ASSETS,
  PLAYER_SKIN_ASSETS,
  loadAnimMeta,
  loadAudioManifest,
  loadEffectSprites,
  loadEnvironmentTileset,
  loadPickupAssets,
} from '../assets/registry';
import { loadSpriteSheet } from '../anim/spritesheet';
import type { FrameEvent } from '../anim/schema';
import { SfxEngine } from '../audio/sfx';
import { WebAudioBackend } from '../audio/webAudioBackend';
import { type GameEvent, type PlayerAttackStyle } from '../game/gameEvents';
import { appearanceAssetIdForCosmetics } from '../cosmetics/visuals';
import type { AppState } from '../state/store';
import { CanvasStage, type CanvasStageControls, type LoadedSpriteAsset } from './canvasStage';
import { EffectsController } from './effects';
import { GameFeel } from './gameFeel';

function reducedMotionPreferred(): boolean {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

function storageOrUndefined(): Storage | undefined {
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}

/** The run the canvas stage should render for the current view (live run or read-only spectate frame). */
function runForView(state: AppState): RunState | null {
  if (state.view === 'run') return state.run;
  if (state.view === 'spectate' && state.spectateSession) {
    const frame = state.spectateSession.frames[state.spectateSession.index];
    return frame ? spectateRenderRun(frame.run) : null;
  }
  return null;
}

export function equippedCosmeticsForRunCanvas(state: AppState): EquippedCosmetic[] {
  if (state.view !== 'run') return [];
  if (state.equippedCosmetics) return state.equippedCosmetics;
  const selfId = state.hub?.self.accountId;
  return state.hub?.presence.find((entry) => entry.accountId === selfId)?.cosmetics ?? [];
}

const CRYPT_SURVEYOR_APPEARANCE_ASSET_ID = 'player_skin_crypt_surveyor_01';

export function playerAttackStyleForCosmetics(cosmetics: readonly EquippedCosmetic[] | null | undefined): PlayerAttackStyle {
  return appearanceAssetIdForCosmetics(cosmetics) === CRYPT_SURVEYOR_APPEARANCE_ASSET_ID ? 'luggage' : 'default';
}

export class BrowserGameFeel {
  readonly facade: GameFeel;
  private readonly effects: EffectsController;
  private readonly stage: CanvasStage;
  private readonly backend = new WebAudioBackend();
  private sfxKeysValue: string[] = [];
  private readonly devMode: boolean;
  private playerAttackStyle: PlayerAttackStyle = 'default';

  constructor(devMode: boolean) {
    this.devMode = devMode;
    this.effects = new EffectsController({ reducedMotion: reducedMotionPreferred() });
    this.stage = new CanvasStage({
      effects: this.effects,
      onFrameEvents: (events, context) => this.handleFrameEvents(events, context),
    });
    this.facade = new GameFeel({ stage: this.stage, effects: this.effects });
    void this.loadAssets();
    void this.loadEnvironment();
    void this.loadPickups();
    void this.loadEffects();
    void this.loadAudio();
  }

  afterRender(root: HTMLElement, state: AppState): void {
    const equipped = equippedCosmeticsForRunCanvas(state);
    this.stage.mount(root, this.devMode && state.devMode, this.controls());
    this.stage.setEquippedCosmetics(equipped);
    this.playerAttackStyle = playerAttackStyleForCosmetics(equipped);
    this.facade.refresh(runForView(state));
  }

  handleActionResult(action: RunAction, prev: RunState, result: ActionResult): GameEvent[] {
    return this.facade.handleActionResult(action, prev, result, { playerAttackStyle: this.playerAttackStyle });
  }

  handleSpectateFrame(prev: SpectateFrame | null, current: SpectateFrame): GameEvent[] {
    return this.facade.handleSpectateFrame(prev, current);
  }

  clear(): void {
    this.facade.clear();
  }

  async unlockAudio(): Promise<void> {
    await this.backend.unlock();
  }

  private async loadAssets(): Promise<void> {
    const loaded: LoadedSpriteAsset[] = [];
    for (const asset of [...Object.values(ENTITY_ASSETS), ...Object.values(PLAYER_SKIN_ASSETS)]) {
      const meta = await loadAnimMeta(asset.metaUrl);
      const sheet = await loadSpriteSheet({
        meta,
        imageUrl: asset.imageUrl,
        color: asset.color,
        label: asset.label,
      });
      loaded.push({ id: asset.id, meta, sheet, color: asset.color, label: asset.label });
    }
    this.stage.setAssets(loaded);
  }

  private async loadEnvironment(): Promise<void> {
    const tileset = await loadEnvironmentTileset(ENVIRONMENT_TILESETS.catacombs_001);
    this.stage.setEnvironmentTileset(tileset);
  }

  private async loadPickups(): Promise<void> {
    const pickups = await loadPickupAssets(ITEM_PICKUP_ASSETS);
    this.stage.setPickupAssets(pickups);
  }

  private async loadEffects(): Promise<void> {
    const effects = await loadEffectSprites(EFFECT_SPRITE_ASSETS);
    this.stage.setEffectSprites(effects);
  }

  private async loadAudio(): Promise<void> {
    const manifest = await loadAudioManifest();
    this.sfxKeysValue = Object.keys(manifest).sort();
    this.facade.setSfx(new SfxEngine(manifest, {
      backend: this.backend,
      audioBase: '/assets/audio/sfx/',
      storage: storageOrUndefined(),
    }));
  }

  private handleFrameEvents(events: FrameEvent[], context: { entityId: string; position: { x: number; y: number } }): void {
    const translated: GameEvent[] = [];
    for (const event of events) {
      if (event.type === 'sfx' && event.name) translated.push({ kind: 'sfx', name: event.name });
      if (event.type === 'effect' && event.name) translated.push({ kind: 'effect', name: event.name, pos: context.position });
      if (event.type === 'cameraShake') translated.push({ kind: 'shake', intensity: event.intensity ?? 2 });
      if (event.type === 'hitStop') translated.push({ kind: 'hitStop', durationMs: event.durationMs ?? 45 });
      if (event.type === 'spawnFloatingText') {
        this.effects.floatingText(event.text ?? '*', {
          x: context.position.x,
          y: context.position.y,
          durationMs: event.durationMs ?? 700,
          now: typeof performance !== 'undefined' ? performance.now() : Date.now(),
        });
      }
    }
    if (translated.length > 0) this.facade.applyEvents(translated);
  }

  private controls(): CanvasStageControls {
    return {
      sfxKeys: () => this.sfxKeysValue,
      playSfx: (key) => { this.facade.getSfx()?.play(key); },
      isMuted: () => this.facade.getSfx()?.isMuted() ?? false,
      setMuted: (muted) => { this.facade.getSfx()?.setMuted(muted); },
      getMasterVolume: () => this.facade.getSfx()?.getMasterVolume() ?? 1,
      setMasterVolume: (v) => { this.facade.getSfx()?.setMasterVolume(v); },
      getSfxVolume: () => this.facade.getSfx()?.getSfxVolume() ?? 1,
      setSfxVolume: (v) => { this.facade.getSfx()?.setSfxVolume(v); },
      triggerEvents: (events) => this.facade.applyEvents(events),
    };
  }
}
